import { memo, type FC } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utils/numberFormat";
import CampaignStepCard from "../../../shared/CampaignStepCard";
import ActionButtonsConfig from "../components/ActionButtonsConfig";
import LiveDevicePreview from "../components/LiveDevicePreview";
import MediaAttachmentField from "../components/MediaAttachmentField";
import ParameterField from "../components/ParameterField";
import SelectedTemplateSummary from "../components/SelectedTemplateSummary";
import { WHATSAPP_EDITOR_KEY } from "../WhatsAppCampaignConstants";
import type {
  WhatsAppAudienceState,
  WhatsAppMediaState,
  WhatsAppPreviewState,
  WhatsAppTemplate,
  WhatsAppVariableDraft,
  WhatsAppVariableDrafts,
} from "../WhatsAppCampaignTypes";
import { getBodyVariableCount, getVariableRowKeys } from "../WhatsAppCampaignUtils";

const STEP_KEY = `${WHATSAPP_EDITOR_KEY}.editor`;
const MAPPING_KEY = `${WHATSAPP_EDITOR_KEY}.mapping`;

interface WhatsAppEditorStepProps {
  template: WhatsAppTemplate;
  drafts: WhatsAppVariableDrafts;
  audience: WhatsAppAudienceState;
  media: WhatsAppMediaState;
  preview: WhatsAppPreviewState;
  onDraftChange: (_key: string, _draft: Partial<WhatsAppVariableDraft>) => void;
  onAttachMedia: (_file: File) => void;
  onRemoveMedia: () => void;
  onChangeTemplate: () => void;
}

const WhatsAppEditorStep: FC<WhatsAppEditorStepProps> = ({
  template,
  drafts,
  audience,
  media,
  preview,
  onDraftChange,
  onAttachMedia,
  onRemoveMedia,
  onChangeTemplate,
}) => {
  const { t, i18n } = useTranslation();
  const { recipientCount, invalidPhoneCount } = audience;
  const bodyVariableCount = getBodyVariableCount(template);

  return (
    <CampaignStepCard
      wide
      title={t(`${STEP_KEY}.title`)}
      subtitle={t(`${STEP_KEY}.subtitle`)}
    >
      <SelectedTemplateSummary template={template} onChangeTemplate={onChangeTemplate} />

      <div className="wa-editor__workspace">
        <section className="wa-mapping">
          <header className="wa-mapping__header">
            <div className="wa-mapping__heading">
              <h3 className="wa-mapping__title">{t(`${MAPPING_KEY}.title`)}</h3>
              <p className="wa-mapping__subtitle">{t(`${MAPPING_KEY}.subtitle`)}</p>
              {audience.status === "error" && (
                <p role="alert" className="wa-mapping__note wa-mapping__note--error">
                  {t(`${MAPPING_KEY}.audienceError`)}
                </p>
              )}
              {recipientCount !== null && (
                <p className="wa-mapping__note">
                  {t(`${MAPPING_KEY}.recipients`, {
                    count: recipientCount,
                    formattedCount: formatNumber(recipientCount, {}, i18n.language),
                  })}
                </p>
              )}
              {invalidPhoneCount !== null && invalidPhoneCount > 0 && (
                <p className="wa-mapping__note">
                  {t(`${MAPPING_KEY}.invalidPhones`, {
                    count: invalidPhoneCount,
                    formattedCount: formatNumber(invalidPhoneCount, {}, i18n.language),
                  })}
                </p>
              )}
            </div>
            <span className="wa-parameter__tag wa-parameter__tag--pill">
              {t(`${MAPPING_KEY}.parameterCount`, {
                count: bodyVariableCount,
                formattedCount: formatNumber(bodyVariableCount, {}, i18n.language),
              })}
            </span>
          </header>

          {getVariableRowKeys(template).map(key => (
            <ParameterField
              key={key}
              variableKey={key}
              draft={drafts[key]}
              columns={audience.columns}
              onChange={onDraftChange}
            />
          ))}

          {template.mediaType && (
            <MediaAttachmentField
              mediaType={template.mediaType}
              media={media}
              onAttach={onAttachMedia}
              onRemove={onRemoveMedia}
            />
          )}

          <ActionButtonsConfig
            buttons={template.buttons}
            drafts={drafts}
            onDraftChange={onDraftChange}
          />
        </section>

        <LiveDevicePreview template={template} attachment={media.attachment} preview={preview} />
      </div>
    </CampaignStepCard>
  );
};

export default memo(WhatsAppEditorStep);
